import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Header from "../../components/header";
import { API_BASE_URL } from "../../utils/CONSTANTS";

const today = () => new Date().toISOString().split("T")[0];

const WireStockAdjustment = () => {
  const [wires, setWires] = useState([]);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    wireName: "",
    adjustmentType: "increase",
    quantity: "",
    adjustmentDate: today(),
    remark: "",
  });

  const selectedWire = useMemo(
    () => wires.find((w) => w.name === form.wireName) || null,
    [wires, form.wireName]
  );

  const currentQty = selectedWire ? Number(selectedWire.wirequantity || 0) : null;

  const previewNewQty = useMemo(() => {
    if (currentQty == null) return null;
    const qty = Number(form.quantity);
    if (isNaN(qty) || qty <= 0) return null;
    return form.adjustmentType === "increase" ? currentQty + qty : currentQty - qty;
  }, [currentQty, form.quantity, form.adjustmentType]);

  const loadWires = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/wire/getwireall`, {
        withCredentials: true,
      });
      const list = res.data?.names || [];
      const extractNumber = (str) => {
        const match = String(str).match(/[\d.]+/);
        return match ? parseFloat(match[0]) : 0;
      };
      setWires(
        [...list].sort((a, b) => extractNumber(a.name) - extractNumber(b.name))
      );
    } catch (err) {
      toast.error("Failed to load wires");
    }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/wire/stock-adjustment`, {
        params: { limit: 30 },
        withCredentials: true,
      });
      setHistory(res.data?.adjustments || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadWires();
    loadHistory();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.wireName) {
      toast.error("Select a wire");
      return;
    }
    const qty = Number(form.quantity);
    if (isNaN(qty) || qty <= 0) {
      toast.error("Enter a valid quantity greater than 0");
      return;
    }
    if (form.adjustmentType === "decrease" && currentQty != null && qty > currentQty) {
      toast.error(`Cannot decrease more than available stock (${currentQty} Kg)`);
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/wire/stock-adjustment`,
        {
          wireName: form.wireName,
          adjustmentType: form.adjustmentType,
          quantity: qty,
          adjustmentDate: form.adjustmentDate,
          remark: form.remark.trim(),
        },
        { withCredentials: true }
      );
      toast.success(res.data?.message || "Stock adjusted");
      setForm((prev) => ({
        ...prev,
        quantity: "",
        remark: "",
        adjustmentDate: today(),
      }));
      await Promise.all([loadWires(), loadHistory()]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to adjust stock");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-GB");
  };

  return (
    <>
      <Header />
      <div className="p-4 max-w-5xl mx-auto">
        <div className="bg-[#e6ffff] border border-gray-300 rounded shadow">
          <div className="px-3 py-2 border-b border-gray-300">
            <h2 className="text-base font-semibold">Wire Stock Adjustment</h2>
            <p className="text-xs text-gray-600">
              Manually increase or decrease stock for any wire
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Wire Name</label>
              <select
                name="wireName"
                value={form.wireName}
                onChange={handleChange}
                className="w-full border border-gray-400 rounded px-2 py-2 bg-white"
                required
              >
                <option value="">Select wire</option>
                {wires.map((w) => (
                  <option key={w.name} value={w.name}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Current Stock (Kg)</label>
              <input
                type="text"
                readOnly
                value={currentQty == null ? "" : currentQty}
                className="w-full border border-gray-300 rounded px-2 py-2 bg-gray-100"
                placeholder="Select a wire"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Adjustment Type</label>
              <select
                name="adjustmentType"
                value={form.adjustmentType}
                onChange={handleChange}
                className="w-full border border-gray-400 rounded px-2 py-2 bg-white"
              >
                <option value="increase">Increase (+)</option>
                <option value="decrease">Decrease (−)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Quantity (Kg)</label>
              <input
                type="number"
                name="quantity"
                min="0.001"
                step="any"
                value={form.quantity}
                onChange={handleChange}
                className="w-full border border-gray-400 rounded px-2 py-2 bg-white"
                placeholder="Enter quantity"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Date</label>
              <input
                type="date"
                name="adjustmentDate"
                value={form.adjustmentDate}
                onChange={handleChange}
                className="w-full border border-gray-400 rounded px-2 py-2 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">New Stock (preview)</label>
              <input
                type="text"
                readOnly
                value={
                  previewNewQty == null
                    ? ""
                    : previewNewQty < 0
                      ? "Invalid (below 0)"
                      : previewNewQty
                }
                className={`w-full border rounded px-2 py-2 bg-gray-100 ${
                  previewNewQty != null && previewNewQty < 0
                    ? "border-red-400 text-red-600"
                    : "border-gray-300"
                }`}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Remark</label>
              <input
                type="text"
                name="remark"
                value={form.remark}
                onChange={handleChange}
                className="w-full border border-gray-400 rounded px-2 py-2 bg-white"
                placeholder="Reason for adjustment (optional)"
              />
            </div>

            <div className="md:col-span-2 flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="bg-teal-700 hover:bg-teal-800 text-white px-4 py-2 rounded disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save Adjustment"}
              </button>
              <button
                type="button"
                onClick={() =>
                  setForm({
                    wireName: "",
                    adjustmentType: "increase",
                    quantity: "",
                    adjustmentDate: today(),
                    remark: "",
                  })
                }
                className="border border-gray-400 px-4 py-2 rounded bg-white hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </form>
        </div>

        <div className="mt-4 bg-white border border-gray-300 rounded shadow overflow-x-auto">
          <div className="px-3 py-2 border-b bg-gray-50 font-semibold text-sm">
            Recent Adjustments
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 text-left border-b">Date</th>
                <th className="p-2 text-left border-b">Wire</th>
                <th className="p-2 text-left border-b">Type</th>
                <th className="p-2 text-right border-b">Qty (Kg)</th>
                <th className="p-2 text-right border-b">Before</th>
                <th className="p-2 text-right border-b">After</th>
                <th className="p-2 text-left border-b">Remark</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-3 text-center text-gray-500">
                    No adjustments yet
                  </td>
                </tr>
              ) : (
                history.map((row) => (
                  <tr key={row._id} className="hover:bg-gray-50">
                    <td className="p-2 border-b">{formatDate(row.adjustmentDate)}</td>
                    <td className="p-2 border-b">{row.wireName}</td>
                    <td className="p-2 border-b capitalize">
                      <span
                        className={
                          row.adjustmentType === "increase"
                            ? "text-green-700"
                            : "text-red-700"
                        }
                      >
                        {row.adjustmentType}
                      </span>
                    </td>
                    <td className="p-2 border-b text-right">{row.quantity}</td>
                    <td className="p-2 border-b text-right">{row.previousQuantity}</td>
                    <td className="p-2 border-b text-right">{row.newQuantity}</td>
                    <td className="p-2 border-b">{row.remark || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default WireStockAdjustment;
